import { useCallback, useEffect, useRef, useState } from 'react';
import useApp from './useApp';
import { useToast } from './useToast';

const VERSION_CHECK_INTERVAL = 30 * 60 * 1000; // 30 minutes
const API_BASE_URL =
  import.meta.env.VITE_API_SERVER?.replace(/\/rcg[cp]?$/, '') ||
  'https://api.retainerclub.com';

export function useVersionCheck() {
  const { app } = useApp();
  const { setToast } = useToast();
  const [isChecking, setIsChecking] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [serverVersion, setServerVersion] = useState(null);
  const intervalRef = useRef(null);
  const hasShownUpdateToast = useRef(false);
  const isCheckingRef = useRef(false);

  // Only run version checks in production or staging environments
  const shouldCheckVersion =
    import.meta.env.VITE_APP_ENV === 'production' ||
    import.meta.env.VITE_APP_ENV === 'staging';

  const getAuthHeaders = useCallback(async () => {
    const authSession = await import('aws-amplify/auth').then((m) =>
      m.fetchAuthSession(),
    );
    const idToken = authSession.tokens?.idToken;

    const headers = {
      'Content-Type': 'application/json',
      'x-app-name': app,
    };

    // Only add Authorization header if idToken exists
    if (idToken) {
      headers.Authorization = `Bearer ${idToken}`;
    }

    return headers;
  }, [app]);

  const checkVersion = useCallback(async () => {
    if (!shouldCheckVersion) {
      return;
    }
    if (!app) {
      return;
    }
    if (isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;
    setIsChecking(true);

    try {
      // Get current version from environment variable
      const localVersion = import.meta.env.VITE_APP_VERSION;
      setCurrentVersion(localVersion);

      // Get server version
      const headers = await getAuthHeaders();

      const response = await fetch(
        `${API_BASE_URL}/api/versions/front-end?app=${app}`,
        {
          method: 'GET',
          headers,
        },
      );

      if (response.ok) {
        const data = await response.json();
        const serverVer = data?.version;
        setServerVersion(serverVer);

        // Compare versions
        if (
          serverVer &&
          localVersion &&
          serverVer !== localVersion &&
          !hasShownUpdateToast.current
        ) {
          hasShownUpdateToast.current = true;

          let message =
            'A new version is available. Click "Update Now" to refresh the page.';

          if (app === 'rc_admin') {
            message = `A new version (${serverVer}) is available. Your current version is ${localVersion}. Click "Update Now" to refresh the page.`;
          }

          setToast({
            show: true,
            title: 'Update Available',
            message,
            type: 'info',
            info: true,
            actionText: 'Update Now',
            onAction: () => window.location.reload(),
          });
        }
      }
    } catch (error) {
      console.error('[VersionCheck] Error checking version:', error);
    } finally {
      isCheckingRef.current = false;
      setIsChecking(false);
    }
  }, [app, setToast, getAuthHeaders]);

  useEffect(() => {
    if (!shouldCheckVersion) {
      return;
    }
    if (!app) {
      return;
    }

    // Initial check
    checkVersion();

    // Set up interval for periodic checks
    intervalRef.current = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [app, checkVersion]);

  return {
    isChecking,
    currentVersion,
    serverVersion,
    checkVersion,
  };
}
